import type * as api from '@gitbook/api-client';
import { Block, Schema } from '@gitbook/slate';
import { isUrl } from '@gitbook/util-url';

import {
    BLOCK_COLUMN,
    BLOCK_INTEGRATION,
    BLOCK_TABS_ITEM,
    type BlockIntegrationData,
} from '../types';

/**
 * Schema to validate custom blocks.
 */
export const schema = Schema.create({
    blocks: {
        [BLOCK_INTEGRATION]: {
            parent: {
                objects: ['document', 'block'],
                types: [undefined, BLOCK_TABS_ITEM, BLOCK_COLUMN],
            },
            isVoid: true,
            data: {
                integration: (value) => typeof value === 'string',
                block: (value) => typeof value === 'string',
                props: (value) => typeof value === 'object',
                action: (value) =>
                    typeof value === 'undefined' ||
                    (typeof value === 'object' && typeof value.action === 'string'),

                url: (value) =>
                    typeof value === 'undefined' || (typeof value === 'string' && isUrl(value)),
                fullWidth: (fullWidth) =>
                    typeof fullWidth === 'undefined' || typeof fullWidth === 'boolean',
            },
        },
    },
});

/**
 * Utility to create a custom block.
 */
export function createIntegrationBlock(
    integration: string,
    block: string,
    props: object = {},
    action: api.ContentKitAction | undefined = undefined,
    fullWidth?: boolean
): Block {
    const data: BlockIntegrationData = {
        integration,
        block,
        props,
        ...(action ? { action } : {}),
        ...(typeof fullWidth !== 'undefined' ? { fullWidth } : {}),
    };

    return Block.createVoid({
        type: BLOCK_INTEGRATION,
        data,
    });
}

/**
 * Utility to create an integration block when unfurling an url
 */
export function createIntegrationBlockForLinkUnfurl(
    integration: string,
    block: string,
    url: string
): Block {
    return Block.createVoid({
        type: BLOCK_INTEGRATION,
        data: createIntegrationBlockDataForLinkUnfurl(integration, block, url),
    });
}

/**
 * Utility to create a the data for an integration block when unfurling an url
 */
export function createIntegrationBlockDataForLinkUnfurl(
    integration: string,
    block: string,
    url: string
): BlockIntegrationData {
    const data: BlockIntegrationData = {
        integration,
        block,
        props: {},
        action: {
            action: '@link.unfurl',
            url,
        },
        url,
    };

    return data;
}
